import { Fragment } from 'react';
import { STATIONS, STATION_ORDER } from '../game/content';
import { bottleneckRecommendation, managerEfficiency, pipelineStatus, stationCapacity } from '../game/simulation';
import type { GameState, InventoryStage, StationId } from '../game/types';

interface PipelineDockProps {
  state: GameState;
  selectedStation: StationId;
  onSelect: (stationId: StationId) => void;
}

const FLOW_LABELS: Record<InventoryStage, string> = {
  grapes: 'GRAPES',
  juice: 'JUICE',
  wine: 'WINE',
  cases: 'CASES'
};

function stageTotal(state: GameState, stage: InventoryStage): number {
  return Object.values(state.inventory).reduce((sum, inventory) => sum + inventory[stage], 0);
}

export function PipelineDock({ state, selectedStation, onSelect }: PipelineDockProps) {
  const reports = pipelineStatus(state);
  const statuses = new Map(reports.map((status) => [status.stationId, status]));
  const recommendation = bottleneckRecommendation(state);
  const blocked = reports.some((status) => status.kind === 'blocked');
  const insightClass = blocked ? 'blocked' : recommendation ? 'guidance' : '';

  return <div className="pipeline-control">
    <div className={`pipeline-insight ${insightClass}`} role="status">
      <span className="pipeline-insight-icon">{blocked ? '!' : recommendation ? '→' : '✓'}</span>
      <div><b>{recommendation?.label ?? 'Pipeline is balanced'}</b><small>{recommendation?.explanation ?? 'No station has been persistently full or starved. Keep improving the slowest stage as demand grows.'}</small></div>
      {recommendation && <button onClick={() => onSelect(recommendation.stationId)}>Show station</button>}
    </div>
    <nav className="pipeline-dock" aria-label="Production pipeline">
      {STATION_ORDER.map((stationId, index) => {
        const def = STATIONS[stationId];
        const station = state.stations[stationId];
        const status = statuses.get(stationId);
        const outputStage = def.output === 'cash' ? 'cases' : def.output;
        const stored = stageTotal(state, outputStage);
        const capacity = stationId === 'sales' ? Math.max(stored, 1) : stationCapacity(state, stationId);
        const fill = stationId === 'sales' ? Math.min(100, stored * 8) : Math.min(100, stored / Math.max(1, capacity) * 100);
        const efficiency = managerEfficiency(state, stationId);
        return <Fragment key={stationId}>
          <button className={`pipeline-station ${selectedStation === stationId ? 'selected' : ''} status-${status?.kind ?? 'manual'}`} onClick={() => onSelect(stationId)}>
            <span className="pipeline-station-icon">{def.icon}</span>
            <span className="pipeline-station-name">{def.shortLabel}</span>
            <small>LEVEL {station.level}</small>
            <div className="pipeline-buffer" aria-label={`${stored.toFixed(1)} ${FLOW_LABELS[outputStage].toLowerCase()} buffered`}><i style={{ width: `${fill}%` }} /></div>
            <b>{status?.label ?? 'Manual'}</b>
            <em>{station.managerLevel > 0 ? `Manager ${station.managerLevel} · ${Math.round(efficiency * 100)}%` : 'Owner operated'}</em>
          </button>
          {index < STATION_ORDER.length - 1 && <div className="pipeline-arrow" aria-hidden="true"><span>→</span><small>{FLOW_LABELS[outputStage]}</small><b>{stored.toFixed(stored >= 100 ? 0 : 1)}</b></div>}
        </Fragment>;
      })}
    </nav>
  </div>;
}
